import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Edit2, ChevronLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import { PropertySidebarNav, PropertyShellHeader, useSidebarScale } from "@/components/owner/PropertySidebarNav";
import ChecklistTab from "@/components/owner/ChecklistTab";
import AutomationsTab from "@/components/owner/AutomationsTab";
import InventoryTab from "@/components/owner/InventoryTab";
import UnitSetupTab from "@/components/owner/UnitSetupTab";
import RoomsTab from "@/components/owner/RoomsTab";
import ReportsTab from "@/components/owner/ReportsTab";
import { SimpleIssuesInbox } from "@/components/owner/SimpleIssuesInbox";
import ScheduleTab from "@/components/owner/ScheduleTab";
import { MyTasksPanel } from "@/components/owner/MyTasksPanel";
import CleanerPerformanceTab from "@/components/owner/CleanerPerformanceTab";
import PaymentsTab from "@/components/owner/PaymentsTab";
import SettingsTab from "@/components/owner/SettingsTab";
import AccessTab from "@/components/owner/AccessTab";
import OverviewTab from "@/components/owner/OverviewTab";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageContainer } from "@/components/layout/PageContainer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { getAllowedTabs, canOpenPropertyTab, canOperateTasks, canEditPropertyConfig, type PropertyTabId, type PropertyUserRole, type PropertyAccessLevel } from "@/lib/propertyTabs";
import { hasTeamPermission } from "@/lib/teamPermissions";
import { AppTour } from "@/components/tour/AppTour";
import { TestCleanLauncher } from "@/components/owner/TestCleanLauncher";
import { usePropertyDetailTour } from "@/hooks/usePropertyDetailTour";

const PropertyDetail = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, loading: authLoading } = useAuth();
  const ctx = usePropertyContext();
  const { data: property, isLoading, error } = usePropertyWithAccess(propertyId);
  const [activeTab, setActiveTab] = useState<PropertyTabId>("overview");
  const sidebarScale = useSidebarScale();
  const [propertyMenuOpen, setPropertyMenuOpen] = useState(false);

  const role = ctx?.role ?? "owner";
  const accessLevel = ctx?.accessLevel ?? "full";
  const permissions = ctx?.permissions ?? null;
  const isOwner = ctx?.isOwner ?? true;
  const accessibleProperties = ctx?.accessibleProperties ?? [];
  const tabItems = getAllowedTabs(role, accessLevel, permissions);
  const allowedTabValues = tabItems.map((t) => t.value);
  const canOpenActiveTab = canOpenPropertyTab(activeTab, role, accessLevel, permissions);
  const tabValue = (canOpenActiveTab ? activeTab : allowedTabValues[0]) ?? "overview";

  // Schedule-only (etc.): default activeTab is overview — sync to first allowed so content renders.
  // Deep-link tabs (checklist, etc.) stay open even when hidden from MVP nav.
  useEffect(() => {
    if (!allowedTabValues.length) return;
    if (canOpenPropertyTab(activeTab, role, accessLevel, permissions)) return;
    const next = allowedTabValues[0]!;
    setActiveTab(next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", next);
      return params;
    }, { replace: true });
  }, [activeTab, allowedTabValues.join(","), role, accessLevel, permissions, setSearchParams]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "billing" || t === "invoices") {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "payments");
        if (t === "invoices" && prev.get("invoiceId")) next.set("invoiceId", prev.get("invoiceId")!);
        return next;
      });
      setActiveTab("payments");
      return;
    }
    if (t && canOpenPropertyTab(t as PropertyTabId, role, accessLevel, permissions)) {
      setActiveTab(t as PropertyTabId);
    }
  }, [searchParams.get("tab"), allowedTabValues.join(","), role, accessLevel, permissions]);

  // Only scroll to Access & Team when the URL explicitly asks for it via hash (e.g. from onboarding),
  // so that Back from /account does not scroll the user away from their previous position.
  const didScrollToAccessRef = useRef(false);
  useEffect(() => {
    if (activeTab !== "settings" || didScrollToAccessRef.current) return;
    if (typeof window === "undefined" || window.location.hash !== "#access-and-team") return;
    const timer = setTimeout(() => {
      document.getElementById("access-and-team")?.scrollIntoView({ behavior: "smooth", block: "start" });
      didScrollToAccessRef.current = true;
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const handleTabChange = useCallback(
    (value: string) => {
      const tab = value as PropertyTabId;
      setActiveTab(tab);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      });
    },
    [setSearchParams]
  );

  const { runTour, steps: tourSteps, closeTour } = usePropertyDetailTour(
    propertyId,
    property ?? null,
    allowedTabValues,
    handleTabChange
  );

  const showEditButton = canEditPropertyConfig(role, accessLevel, permissions);
  const propertiesListHref = "/owner/properties";

  // Wait for auth so profile and property access are available
  if (authLoading) {
    return (
      <AppLayout>
        <Navbar />
        <PageContainer className="py-4 sm:py-6">
          <p className="text-muted-foreground">Loading...</p>
        </PageContainer>
      </AppLayout>
    );
  }
  if (isLoading) {
    return (
      <AppLayout>
        <Navbar />
        <PageContainer className="py-4 sm:py-6">
          <p className="text-muted-foreground">Loading property...</p>
        </PageContainer>
      </AppLayout>
    );
  }
  if (error || !property) {
    return (
      <AppLayout>
        <Navbar />
        <PageContainer className="py-4 sm:py-6">
          {propertiesListHref && (
            <Button variant="ghost" onClick={() => navigate(propertiesListHref)} className="mb-4">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <p className="text-destructive">Property not found</p>
        </PageContainer>
      </AppLayout>
    );
  }

  const tabTitle = tabItems.find((t) => t.value === tabValue)?.label ?? "";
  const shellName = (property as { unit?: string }).unit
    ? `${property.name} · ${(property as { unit?: string }).unit}`
    : property.name;

  return (
    <AppLayout>
      <div
        className="flex h-screen flex-col overflow-hidden bg-[#f4f7f9] pl-0 lg:pl-[var(--sbpad)]"
        style={{ "--sbpad": `${16 + 260 * sidebarScale + 44}px` } as React.CSSProperties}
      >
        <PropertySidebarNav
          tabItems={tabItems}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          propertyId={propertyId ?? ""}
          propertyName={shellName}
          propertiesHref={propertiesListHref}
          roleLabel={role === "owner" ? "Owner" : "Co-host"}
          mobileOpen={propertyMenuOpen}
          onMobileOpenChange={setPropertyMenuOpen}
        />
        <div className="flex min-h-0 w-full flex-1 flex-col px-[18px] pt-3 lg:px-0 lg:pb-4 lg:pl-0 lg:pr-10 lg:pt-[34px]">
          <PropertyShellHeader
            propertyName={shellName}
            title={tabTitle}
            onOpenMenu={() => setPropertyMenuOpen(true)}
          />
          <div className="owner-v2 min-h-0 flex-1 overflow-y-auto pb-8 pt-7 max-lg:pb-6 max-lg:pt-1.5" data-tour-scroll-root>
            <Tabs value={tabValue} onValueChange={handleTabChange} className="w-full">
          <TabsContent value="overview" className="mt-2">
            {tabValue === "overview" && (
              <OverviewTab
                propertyId={propertyId!}
                property={{
                  name: property.name,
                  address: property.address ?? undefined,
                  unit: (property as { unit?: string }).unit,
                  city: (property as { city?: string }).city,
                  state_province: (property as { state_province?: string }).state_province,
                  postal_code: (property as { postal_code?: string }).postal_code,
                  property_photo_url: property.property_photo_url ?? undefined,
                  property_photo_urls: (property as { property_photo_urls?: string[] | null }).property_photo_urls,
                  hospitable_property_id: (property as { hospitable_property_id?: string | null })
                    .hospitable_property_id,
                  owner_id: (property as { owner_id?: string }).owner_id,
                  entry_method: (property as { entry_method?: string | null }).entry_method ?? null,
                  entry_code: (property as { entry_code?: string | null }).entry_code ?? null,
                  entry_location: (property as { entry_location?: string | null }).entry_location ?? null,
                  access_notes: (property as { access_notes?: string | null }).access_notes ?? null,
                  amazon_buy_list_url:
                    (property as { amazon_buy_list_url?: string | null }).amazon_buy_list_url ?? null,
                  setup_checklist_state: (property as { setup_checklist_state?: unknown }).setup_checklist_state,
                }}
                onTabChange={handleTabChange}
              />
            )}
          </TabsContent>

          <TabsContent value="schedule" className="mt-2">
            {tabValue === "schedule" && propertyId && (
              <ErrorBoundary>
                <ScheduleTab
                  propertyId={propertyId}
                  hasIcalUrls={!!(
                    property.airbnb_ical_url ||
                    property.vrbo_ical_url ||
                    property.booking_ical_url ||
                    (property as { hospitable_property_id?: string | null }).hospitable_property_id
                  )}
                  onConnectIcal={() => handleTabChange("settings")}
                  canAddTasks={canOperateTasks(role, accessLevel, permissions)}
                  focusReservationId={searchParams.get("focusReservationId") ?? undefined}
                  focusTaskId={searchParams.get("focusTaskId") ?? undefined}
                  onGoToUnit={() => handleTabChange("unit")}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="my-tasks" className="mt-2">
            {tabValue === "my-tasks" && propertyId && (
              <ErrorBoundary>
                <MyTasksPanel
                  propertyId={propertyId}
                  title="My Tasks"
                  canExecuteTasksForTeam={
                    isOwner || hasTeamPermission(permissions, "tasks")
                  }
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="checklist" className="mt-2">
            {tabValue === "checklist" && propertyId && (
              <ErrorBoundary>
                <ChecklistTab
                  propertyId={propertyId}
                  editChecklistId={searchParams.get("editChecklistId")}
                  onClearEditChecklistId={() => {
                    setSearchParams((prev) => {
                      const n = new URLSearchParams(prev);
                      n.delete("editChecklistId");
                      return n;
                    });
                  }}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="inventory" className="mt-2">
            {tabValue === "inventory" && propertyId && (
              <ErrorBoundary>
                <InventoryTab
                  propertyId={propertyId}
                  className="px-px"
                  focusItemId={searchParams.get("focusItemId") ?? undefined}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="unit" className="mt-2">
            {tabValue === "unit" && propertyId && (
              <ErrorBoundary>
                <UnitSetupTab propertyId={propertyId} className="px-px" />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="rooms" className="mt-2">
            {tabValue === "rooms" && propertyId && (
              <ErrorBoundary>
                <RoomsTab propertyId={propertyId} />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="access" className="mt-2">
            {tabValue === "access" && propertyId && (
              <ErrorBoundary>
                <AccessTab
                  propertyId={propertyId}
                  property={{
                    entry_method: (property as { entry_method?: string | null }).entry_method ?? null,
                    entry_code: (property as { entry_code?: string | null }).entry_code ?? null,
                    entry_location: (property as { entry_location?: string | null }).entry_location ?? null,
                    unit_location: (property as { unit_location?: string | null }).unit_location ?? null,
                    parking_instructions:
                      (property as { parking_instructions?: string | null }).parking_instructions ?? null,
                    supply_closet_info:
                      (property as { supply_closet_info?: string | null }).supply_closet_info ?? null,
                    access_notes: (property as { access_notes?: string | null }).access_notes ?? null,
                    key_location_reference_urls:
                      (property as { key_location_reference_urls?: string[] | null })
                        .key_location_reference_urls ?? [],
                  }}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="payments" className="mt-2">
            {tabValue === "payments" && propertyId && (
              <ErrorBoundary>
                <PaymentsTab
                  propertyId={propertyId}
                  propertyName={property.name}
                  isPropertyOwner={isOwner}
                  role={(role as PropertyUserRole) ?? "owner"}
                  accessLevel={(accessLevel as PropertyAccessLevel) ?? "full"}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="reports" className="mt-2">
            {tabValue === "reports" && propertyId && (
              <ErrorBoundary>
                <ReportsTab
                  propertyId={propertyId}
                  onGoToUnit={() => handleTabChange("unit")}
                  onGoToInventory={() => handleTabChange("inventory")}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="maintenance" className="mt-2">
            {tabValue === "maintenance" && propertyId && (
              <ErrorBoundary>
                <SimpleIssuesInbox
                  propertyId={propertyId}
                  focusIssueId={searchParams.get("focusIssueId") ?? undefined}
                />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="performance" className="mt-2">
            {tabValue === "performance" && propertyId && (
              <ErrorBoundary>
                <CleanerPerformanceTab propertyId={propertyId} />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="automations" className="mt-2">
            {tabValue === "automations" && propertyId && (
              <ErrorBoundary>
                <AutomationsTab propertyId={propertyId} />
              </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-2">
            {tabValue === "settings" && (
              <SettingsTab
                propertyId={propertyId!}
                property={{
                  name: property.name,
                  address: property.address ?? null,
                  unit: (property as { unit?: string }).unit ?? null,
                  city: (property as { city?: string }).city ?? null,
                  state_province: (property as { state_province?: string }).state_province ?? null,
                  postal_code: (property as { postal_code?: string }).postal_code ?? null,
                  notes: (property as { notes?: string }).notes ?? null,
                  created_at: property.created_at,
                  hospitable_property_id:
                    (property as { hospitable_property_id?: string | null }).hospitable_property_id ?? null,
                  amazon_buy_list_url:
                    (property as { amazon_buy_list_url?: string | null }).amazon_buy_list_url ?? null,
                  airbnb_ical_url: property.airbnb_ical_url ?? null,
                  vrbo_ical_url: property.vrbo_ical_url ?? null,
                  booking_ical_url: property.booking_ical_url ?? null,
                  qr_code_url: (property as { qr_code_url?: string }).qr_code_url ?? null,
                  property_code: (property as { property_code?: string }).property_code ?? null,
                  status: (property as { status?: string }).status,
                  archived_at: (property as { archived_at?: string | null }).archived_at ?? null,
                  owner_id: (property as { owner_id?: string }).owner_id,
                  entry_method: (property as { entry_method?: string | null }).entry_method ?? null,
                  entry_code: (property as { entry_code?: string | null }).entry_code ?? null,
                  entry_location: (property as { entry_location?: string | null }).entry_location ?? null,
                  unit_location: (property as { unit_location?: string | null }).unit_location ?? null,
                  parking_instructions: (property as { parking_instructions?: string | null }).parking_instructions ?? null,
                  supply_closet_info: (property as { supply_closet_info?: string | null }).supply_closet_info ?? null,
                  access_notes: (property as { access_notes?: string | null }).access_notes ?? null,
                }}
                isOwner={isOwner}
                accessLevel={accessLevel}
              />
            )}
          </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      {runTour && tourSteps.length > 0 && (
        <AppTour
          tour={null}
          tourId="owner-property-detail"
          steps={tourSteps}
          onClose={closeTour}
        />
      )}
    </AppLayout>
  );
};

export default PropertyDetail;
